import {
  computed,
  Directive,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ChangeCallback, TouchedCallback } from '@fmnts/common/forms';
import { coerceDayInput, coerceMonthInput } from '@fmnts/core/chronos';
import { getUnique } from '@fmnts/core/collection';
import { DateService } from '@fmnts/i18n';

function allSeparatorSymbols(format: string): string[] {
  const separators = format.match(/\W+/g) ?? [];
  return getUnique(separators);
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Directive to be used on HTML `<input>` elements to format Dates
 * according to specified locale setting. The directive deals with
 * the `value` in ISO format (YYYY-MM-DD), so when writing / reading
 * it expects the format to be fulfilled. Otherwise the value is
 * an empty string.
 */
@Directive({
  selector: 'input[nextDateFormat]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateFormatDirective),
      multi: true,
    },
  ],
})
export class DateFormatDirective implements ControlValueAccessor {
  private readonly element = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly dateService = inject(DateService);

  /** Date format for current locale */
  private readonly localizedFormat = this.dateService.dateInputFormat;
  /** Holds the current value as iso formatted date  */
  private dateAsIso: string | null = null;

  private readonly separators = computed(() =>
    allSeparatorSymbols(this.localizedFormat()),
  );

  /**
   * Takes a (partial) input that is localized and coerces it
   * towards the expected format by
   * - inserting separators
   * - clamping or padding days/months
   *
   * @param input Input, that might only be a part of the desired format
   * @param format Localization format
   *
   * @returns
   * Transformed input
   */
  private readonly coerceLocalizedInput = computed<(input: string) => string>(
    () => {
      const format = this.localizedFormat();
      return format ? createDateFormatCoercion(format) : () => '';
    },
  );

  private readonly placeholder = computed(() => this.localizedFormat());

  private onChange: ChangeCallback<string> = () => {};
  private onTouched: TouchedCallback = () => {};

  constructor() {
    effect(() => {
      this.setElementProp('placeholder', this.placeholder());
    });
  }

  writeValue(value: any): void {
    // Parse passed value from iso format
    const parsedValue = this.dateService.moment(value, ISO_DATE_FORMAT, true);

    // If passed value fits the ISO format, update target element
    // and internal state with new value. Otherwise, unset.
    if (parsedValue.isValid()) {
      this.dateAsIso = parsedValue.format(ISO_DATE_FORMAT);
      this.setElementProp('value', parsedValue.format(this.localizedFormat()));
    } else {
      this.dateAsIso = null;
      this.setElementProp('value', '');
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  @HostListener('blur') onBlur() {
    this.onTouched();
  }

  /**
   * Transform inserted Text according to `localizedFormat`
   */
  @HostListener('input', ['$event']) onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;

    const result = this.coerceLocalizedInput()(input.value);
    this.updateValue(result);
  }

  /**
   * Delete Event
   * When last symbol is the separator, it also removes the previous
   * digit
   */
  @HostListener('keydown.backspace', ['$event']) onDelete(
    event: KeyboardEvent,
  ) {
    const input = event.target as HTMLInputElement;

    // no selection, only cursor
    if (input.selectionStart === input.selectionEnd) {
      // Check if the text right before the cursor ends on a non-digit
      // and if so, remove the last digit preceding the cursor
      // e.g. `13.|08.` -> `1|.08.`
      // e.g. `13.08.|` -> `13.0|`
      const textBeforeCursor = input.value.substring(0, input.selectionEnd);
      for (const separator of this.separators()) {
        if (!textBeforeCursor.endsWith(separator)) {
          continue;
        }

        // Get the slices before and after the text that should be removed
        const sliceBefore = textBeforeCursor.substring(
          0,
          textBeforeCursor.length - 1 - separator.length,
        );
        const sliceAfter =
          input.value.length === input.selectionEnd
            ? ''
            : input.value.substring(sliceBefore.length + 1);

        // Set new value, update selection and prevent browser from
        // deleting more chararcters
        this.updateValue(sliceBefore + sliceAfter);
        input.setSelectionRange(sliceBefore.length, sliceBefore.length);
        event.preventDefault();
        break;
      }
    }
  }

  /**
   * Updates the target element using Renderer and
   * triggers the onChange event for the ControlValueAccessor
   *
   * @param value The localized date value
   */
  private updateValue(value: string) {
    this.setElementProp('value', value);

    const parsed = this.dateService.moment(value, this.localizedFormat(), true);
    const newIsoDate = parsed.isValid() ? parsed.format(ISO_DATE_FORMAT) : null;
    // Compare the new date to the current and update if changed
    if (newIsoDate !== this.dateAsIso) {
      this.dateAsIso = newIsoDate;
      this.onChange(newIsoDate ?? '');
    }
  }

  /**
   * Set a property of the Input Field
   *
   * @param property Property of HTMLInputElement that should be set
   * e.g. 'value', or 'placeholder'
   *
   * @param value The value which should be set
   */
  private setElementProp(property: string, value: string) {
    // When the value should be updated, we can strip all letters
    if (property === 'value') {
      const strippedChars = value.replace(/[a-zA-Z]/g, '');
      this._renderer.setProperty(
        this.element.nativeElement,
        property,
        strippedChars,
      );
    } else {
      this._renderer.setProperty(this.element.nativeElement, property, value);
    }
  }
}

function createDateFormatCoercion(format: string) {
  const separator = allSeparatorSymbols(format)[0];
  const groups = format.split(separator);

  return (input: string): string => {
    if (input.length === 0) {
      return '';
    }

    // Group digits according to format
    const inputGroups = input.split(separator).slice(0, groups.length);
    const numbers: string[] = [];
    groups.forEach((group, i) => {
      // Some formats end on a separator (e.g. YYYY.MM.DD.)
      if (group.length === 0 && numbers.length === i + 1) {
        numbers.push('');
        return;
      }

      const groupInput = inputGroups[i] ?? '';
      if (groupInput.length === 0) {
        return; // Nothing more to add
      }

      if (group === 'DD') {
        numbers.push(coerceDayInput(groupInput));
      } else if (group === 'MM') {
        numbers.push(coerceMonthInput(groupInput));
      } else if (group === 'YYYY') {
        numbers.push(groupInput.substring(0, 4));
      }
    });

    // If the format specifies more groups and the the last group is complete
    // add an empty group to get a trailing separator sign
    if (
      numbers.length > 0 && // only if there is any input at all
      groups.length > numbers.length &&
      numbers[numbers.length - 1].length === groups[numbers.length - 1].length
    ) {
      numbers.push('');
    }

    // Join the dates for their final format
    return numbers.join(separator);
  };
}
